import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   ASSETSPATIALASSIGNMENTPANEL.JSX
   ----------------------------------------------------------------------------
   Colonne spatiale dédiée :
   - viewer
   - sélecteur
   - attributions existantes
   - récapitulatif de l'entité sélectionnée
============================================================================ */

const AssetSpatialAssignmentPanel = ({
  title = "Spatial Assignment",
  item = null,
  spatialEntities = [],
  onAddSpatialAssignment,
  onRemoveSpatialAssignment,
  readOnlyMode = false,
  disabled = false,
}) => {
  const [selectedSpatialEntityId, setSelectedSpatialEntityId] = useState("");

  const assignments = Array.isArray(item?.spatialAssignments)
    ? item.spatialAssignments
    : [];

  const enrichedAssignments = useMemo(() => {
    return assignments.map((assignment) => {
      const spatialEntity = spatialEntities.find(
        (entity) => entity.id === assignment.spatialEntityId
      );

      return {
        ...assignment,
        spatialEntity,
      };
    });
  }, [assignments, spatialEntities]);

  const assignedIds = useMemo(() => {
    return new Set(assignments.map((assignment) => assignment.spatialEntityId));
  }, [assignments]);

  const availableEntities = useMemo(() => {
    return spatialEntities.filter((entity) => !assignedIds.has(entity.id));
  }, [spatialEntities, assignedIds]);

  const selectedSpatialEntity = useMemo(() => {
    return (
      availableEntities.find((entity) => entity.id === selectedSpatialEntityId) ||
      null
    );
  }, [availableEntities, selectedSpatialEntityId]);

  const canAdd =
    !readOnlyMode &&
    !disabled &&
    !!item &&
    !!selectedSpatialEntityId &&
    typeof onAddSpatialAssignment === "function";

  const handleAdd = () => {
    if (!canAdd) return;
    onAddSpatialAssignment(item.id, selectedSpatialEntityId);
    setSelectedSpatialEntityId("");
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing.sm,
      }}
    >
      <div
        style={{
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.xs,
          fontWeight: theme.fontWeights.medium,
          color: theme.colors.text.small,
          textTransform: "uppercase",
          letterSpacing: "0.4px",
        }}
      >
        {title}
      </div>

      {/* ------------------------------------------------------------------
         VIEWER
      ------------------------------------------------------------------ */}
      <SpatialEntityViewer entity={selectedSpatialEntity} />

      {/* ------------------------------------------------------------------
         SELECTEUR + ADD
      ------------------------------------------------------------------ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: theme.spacing.sm,
          alignItems: "center",
        }}
      >
        <select
          value={selectedSpatialEntityId}
          disabled={readOnlyMode || disabled || availableEntities.length === 0}
          onChange={(e) => setSelectedSpatialEntityId(e.target.value)}
          style={{
            width: "100%",
            height: 28,
            minHeight: 28,
            padding: "2px 8px",
            boxSizing: "border-box",
            fontFamily: theme.form.field.fontFamily,
            fontSize: theme.form.field.fontSize,
            fontWeight: theme.form.field.fontWeight,
            color: disabled ? theme.colors.text.body : theme.form.field.color,
            borderRadius: theme.borders.radius.md,
            border: `1px solid ${theme.colors.borders.default}`,
            background: disabled
              ? "transparent"
              : theme.colors.background.input,
            outline: "none",
          }}
        >
          <option value="">
            {availableEntities.length > 0
              ? "Select spatial entity..."
              : "No entity available"}
          </option>

          {availableEntities.map((entity) => (
            <option key={entity.id} value={entity.id}>
              {formatEntityOptionLabel(entity)}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          style={actionButtonStyle({
            disabled: !canAdd,
            danger: false,
          })}
        >
          Add
        </button>
      </div>

      {/* ------------------------------------------------------------------
         ATTRIBUTIONS EXISTANTES
      ------------------------------------------------------------------ */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing.xs,
        }}
      >
        {enrichedAssignments.length === 0 ? (
          <div
            style={{
              border: `1px dashed ${theme.colors.borders.default}`,
              borderRadius: theme.borders.radius.lg,
              padding: theme.spacing.sm,
              background: theme.colors.background.Card,
              color: theme.colors.text.small,
              fontFamily: theme.fonts.body,
              fontSize: theme.fontSizes.sm,
              lineHeight: 1.35,
            }}
          >
            No spatial assignment.
          </div>
        ) : (
          enrichedAssignments.map((assignment) => {
            const entity = assignment.spatialEntity;
            const palette = getSpatialTypePalette(
              assignment.spatialType || entity?.spatialType
            );

            return (
              <div
                key={`${assignment.spatialEntityId}-${assignment.allocationRole || "PRIMARY"}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  gap: theme.spacing.sm,
                  alignItems: "center",
                  padding: theme.spacing.sm,
                  borderRadius: theme.borders.radius.lg,
                  border: `1px solid ${theme.colors.borders.default}`,
                  background: theme.colors.background.Card,
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    minWidth: 34,
                    borderRadius: theme.borders.radius.lg,
                    background: palette.lightBg,
                    border: `1px solid ${palette.lightBorder}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SpatialTypeSymbol
                    spatialType={assignment.spatialType || entity?.spatialType}
                    color={palette.color}
                  />
                </div>

                <div
                  style={{
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <div
                    style={{
                      fontFamily: theme.fonts.body,
                      fontSize: theme.fontSizes.sm,
                      fontWeight: theme.fontWeights.medium,
                      color: theme.colors.text.primary,
                      lineHeight: 1.25,
                    }}
                  >
                    {entity?.name || assignment.spatialEntityId}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      fontFamily: theme.fonts.body,
                      fontSize: theme.fontSizes.xs,
                      color: theme.colors.text.small,
                      lineHeight: 1.2,
                    }}
                  >
                    <span>{assignment.spatialType || entity?.spatialType || "—"}</span>

                    {typeof entity?.area === "number" && (
                      <span>Area: {entity.area}</span>
                    )}

                    {typeof entity?.perimeter === "number" && (
                      <span>Perim.: {entity.perimeter}</span>
                    )}

                    {typeof entity?.elevation === "number" && (
                      <span>Elev.: {entity.elevation}</span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  title="Remove assignment"
                  disabled={
                    readOnlyMode ||
                    disabled ||
                    typeof onRemoveSpatialAssignment !== "function"
                  }
                  onClick={() =>
                    onRemoveSpatialAssignment &&
                    onRemoveSpatialAssignment(item.id, assignment.spatialEntityId)
                  }
                  style={actionButtonStyle({
                    disabled:
                      readOnlyMode ||
                      disabled ||
                      typeof onRemoveSpatialAssignment !== "function",
                    danger: true,
                  })}
                >
                  Remove
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* ------------------------------------------------------------------
         RECAPITULATIF ENTITE SELECTIONNEE
      ------------------------------------------------------------------ */}
      <SpatialEntityRecap entity={selectedSpatialEntity} />
    </div>
  );
};

/* ============================================================================
   VIEWER PLEINE LARGEUR
============================================================================ */
const SpatialEntityViewer = ({ entity }) => {
  const palette = entity ? getSpatialTypePalette(entity.spatialType) : null;

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing.md,
      }}
    >
      <div
        style={{
          width: "100%",
          height: 160,
          borderRadius: theme.borders.radius.lg,
          border: `1px solid ${theme.colors.borders.default}`,
          background: "#eeeeee",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {entity ? (
          <ViewerShape
            spatialType={entity.spatialType}
            color={palette.color}
          />
        ) : (
          <span
            style={{
              fontFamily: theme.fonts.body,
              fontSize: theme.fontSizes.sm,
              color: theme.colors.text.small,
            }}
          >
            No spatial entity selected
          </span>
        )}
      </div>
    </div>
  );
};

const SpatialEntityRecap = ({ entity }) => {
  if (!entity) {
    return (
      <div
        style={{
          border: `1px dashed ${theme.colors.borders.default}`,
          borderRadius: theme.borders.radius.lg,
          padding: theme.spacing.sm,
          background: theme.colors.background.Card,
          color: theme.colors.text.small,
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.sm,
          lineHeight: 1.35,
        }}
      >
        No spatial recap available.
      </div>
    );
  }

  const palette = getSpatialTypePalette(entity.spatialType);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing.sm,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontFamily: theme.fonts.heading,
            fontSize: theme.fontSizes.md,
            fontWeight: theme.fontWeights.semiBold,
            color: theme.colors.text.primary,
            lineHeight: 1.2,
          }}
        >
          {entity.name || entity.id}
        </div>

        <span
          style={{
            padding: "4px 8px",
            borderRadius: theme.borders.radius.full,
            background: theme.colors.background.Card,
            border: `1px solid ${theme.colors.borders.default}`,
            fontSize: theme.fontSizes.xs,
            fontWeight: theme.fontWeights.medium,
            color: palette.color,
          }}
        >
          {entity.spatialType}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          gap: theme.spacing.sm,
          flexWrap: "wrap",
        }}
      >
        <ViewerMetric label="Elevation" value={formatMetric(entity.elevation)} />
        <ViewerMetric label="Area" value={formatMetric(entity.area)} />
        <ViewerMetric label="Perimeter" value={formatMetric(entity.perimeter)} />
      </div>
    </div>
  );
};

const ViewerMetric = ({ label, value }) => {
  return (
    <div
      style={{
        minWidth: 0,
        padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
        borderRadius: theme.borders.radius.md,
        background: theme.colors.background.surface,
        border: `1px solid ${theme.colors.borders.default}`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <div
        style={{
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.xs,
          color: theme.colors.text.small,
          lineHeight: 1.2,
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.sm,
          fontWeight: theme.fontWeights.medium,
          color: theme.colors.text.primary,
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </div>
    </div>
  );
};

const ViewerShape = ({ spatialType, color }) => {
  switch (spatialType) {
    case "SITE":
      return (
        <svg width="74" height="74" viewBox="0 0 74 74" fill="none">
          <rect
            x="13"
            y="21"
            width="48"
            height="32"
            rx="5"
            stroke={color}
            strokeWidth="2.2"
            strokeDasharray="4 3"
          />
        </svg>
      );

    case "LEVEL":
      return (
        <svg width="74" height="74" viewBox="0 0 74 74" fill="none">
          <path
            d="M15 43h44"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M22 31h30"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            opacity="0.8"
          />
        </svg>
      );

    case "ZONE":
      return (
        <svg width="74" height="74" viewBox="0 0 74 74" fill="none">
          <rect
            x="21"
            y="17"
            width="32"
            height="32"
            rx="4"
            stroke={color}
            strokeWidth="2.4"
          />
        </svg>
      );

    default:
      return (
        <svg width="74" height="74" viewBox="0 0 74 74" fill="none">
          <circle cx="37" cy="37" r="16" stroke={color} strokeWidth="2.2" />
        </svg>
      );
  }
};

const SpatialTypeSymbol = ({ spatialType, color }) => {
  switch (spatialType) {
    case "SITE":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect
            x="4"
            y="6"
            width="16"
            height="12"
            rx="2"
            stroke={color}
            strokeWidth="1.8"
            strokeDasharray="2 2"
          />
        </svg>
      );

    case "LEVEL":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M4 12h16"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );

    case "ZONE":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect
            x="6"
            y="6"
            width="12"
            height="12"
            rx="2"
            stroke={color}
            strokeWidth="1.8"
          />
        </svg>
      );

    default:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="6" stroke={color} strokeWidth="1.8" />
        </svg>
      );
  }
};

const getSpatialTypePalette = (spatialType) => {
  switch (spatialType) {
    case "SITE":
      return {
        color: theme.Generic.blue.dark.bg,
        lightBg: theme.Generic.blue.pastel.bg,
        lightBorder: theme.Generic.blue.pastel.border,
      };

    case "LEVEL":
      return {
        color: theme.Generic.orange.dark.bg,
        lightBg: theme.Generic.orange.pastel.bg,
        lightBorder: theme.Generic.orange.pastel.border,
      };

    case "ZONE":
      return {
        color: theme.Generic.green.dark.bg,
        lightBg: theme.Generic.green.pastel.bg,
        lightBorder: theme.Generic.green.pastel.border,
      };

    default:
      return {
        color: theme.colors.text.primary,
        lightBg: theme.colors.background.Card,
        lightBorder: theme.colors.borders.default,
      };
  }
};

const formatEntityOptionLabel = (entity) => {
  const parts = [entity.spatialType, entity.name].filter(Boolean);
  return parts.join(" · ");
};

const formatMetric = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const actionButtonStyle = ({ disabled = false, danger = false }) => ({
  height: 28,
  padding: "0 10px",
  borderRadius: theme.borders.radius.full,
  border: `1px solid ${
    disabled
      ? theme.colors.borders.default
      : danger
      ? "#e5c5c5"
      : theme.colors.borders.default
  }`,
  background: disabled
    ? theme.colors.background.input
    : danger
    ? "#fff7f7"
    : theme.colors.background.surface,
  color: disabled
    ? theme.colors.text.verysmall
    : danger
    ? theme.colors.text.danger
    : theme.colors.text.primary,
  fontFamily: theme.fonts.body,
  fontSize: theme.fontSizes.xs,
  fontWeight: theme.fontWeights.medium,
  display: "inline-flex",
  alignItems: "center",
  cursor: disabled ? "default" : "pointer",
  whiteSpace: "nowrap",
});

export default AssetSpatialAssignmentPanel;